import { supabase } from '@/lib/supabase';
import { computeAlertes } from '@/modules/alertes/api';
import { formatEuro } from '@/modules/catalogue/api';
import { CHANTIER_STATUT_LABELS, listChantiers } from '@/modules/chantiers/api';
import { contactDisplayName, listContacts } from '@/modules/clients/api';
import { DEVIS_STATUT_LABELS, listDevis } from '@/modules/devis/api';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

// Construit un instantané COMPACT des données du CRM (filtrées par les droits de
// l'utilisateur, puisque les requêtes passent par son client). Envoyé à
// l'assistant comme contexte pour répondre sans rien inventer.
export async function buildContext(): Promise<string> {
  const [alertesR, devis, chantiers, contacts] = await Promise.all([
    computeAlertes(),
    listDevis(),
    listChantiers(),
    listContacts(),
  ]);

  const nameMap = new Map(contacts.map((c) => [c.id, contactDisplayName(c)]));
  const nameOf = (id: string): string => nameMap.get(id) ?? 'client inconnu';

  const lines: string[] = [];

  lines.push(
    `ALERTES (${alertesR.counts.total} : ${alertesR.counts.haute} haute, ${alertesR.counts.moyenne} moyenne, ${alertesR.counts.basse} basse) :`,
  );
  if (alertesR.alertes.length === 0) lines.push('- aucune');
  for (const a of alertesR.alertes.slice(0, 60)) {
    lines.push(
      `- [${a.priorite}] ${a.titre} — ${a.description}${a.montant ? ` (${formatEuro(a.montant)})` : ''}`,
    );
  }

  lines.push('', `DEVIS (${devis.length}) :`);
  for (const d of devis.slice(0, 120)) {
    lines.push(
      `- ${d.numero} | ${DEVIS_STATUT_LABELS[d.statut] ?? d.statut} | ${formatEuro(d.montant_ttc)} | ${d.objet} | ${nameOf(d.contact_id)} | émis ${d.date_emission}`,
    );
  }

  lines.push('', `CHANTIERS (${chantiers.length}) :`);
  for (const ch of chantiers.slice(0, 120)) {
    lines.push(
      `- ${ch.numero} | ${CHANTIER_STATUT_LABELS[ch.statut] ?? ch.statut} | ${ch.libelle} | ${ch.contact_id ? nameOf(ch.contact_id) : 'sans client'}${ch.ville ? ` | ${ch.ville}` : ''} | ${ch.avancement_pourcentage}%`,
    );
  }

  lines.push('', `CLIENTS (${contacts.length}) :`);
  lines.push(contacts.slice(0, 150).map((c) => contactDisplayName(c)).join(', ') || '(aucun)');

  return lines.join('\n');
}

export async function askAssistant(messages: ChatMessage[], context: string): Promise<string> {
  const { data, error } = await supabase.functions.invoke('assistant', {
    body: { messages, context },
  });
  if (error) throw new Error(error.message);
  const d = (data ?? {}) as { reply?: string; error?: string };
  if (d.error) throw new Error(d.error);
  return d.reply ?? '';
}
